import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { FileStack, Layers, UserCheck, Sparkles, Shield, TrendingUp, Heart, Laptop, ChevronDown, Users } from 'lucide-react'
import { SiteNav, SiteFooter } from '@/components/ui/SiteNav'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { EchoPanelCluster } from '@/components/brand/EchoPanelCluster'
import { DEMO_APPROVED_HEADLINES } from '@/lib/demo/constants'
import type { DemoId } from '@/lib/demo/constants'

const MODULES = [
  { title: 'Study memory', Icon: FileStack, desc: 'Every uploaded study becomes structured evidence — quotes, themes, segments, gaps.' },
  { title: 'Echo Panel', Icon: EchoPanelCluster, desc: 'Segment archetypes built from your real customers, with weak-coverage warnings inline.' },
  { title: 'Twin Panel', Icon: Users, desc: '1:1 modeling per respondent with backtest-validated accuracy — for engagements that need per-customer prediction, not just segment-level signal.' },
  { title: 'Evidence-backed pre-read', Icon: Layers, desc: 'Multiple independent checks that cite evidence and surface disagreement when it matters.' },
  { title: 'Human validation', Icon: UserCheck, desc: 'Recruit real humans through your panel provider to validate the risky segments.' },
  { title: 'Learning record', Icon: Sparkles, desc: 'Every prediction-vs-truth comparison sharpens the next pre-read.' },
] as const

const DIFF_TABLE = [
  { category: 'Generic AI chat', they: 'Summarizes uploaded files', us: 'Builds structured research memory, cites evidence per claim, and routes uncertainty to real-human validation' },
  { category: 'Research repositories', they: 'Organize past evidence', us: 'Uses that evidence to pre-read new decisions, not just to search' },
  { category: 'Synthetic panel tools', they: 'Generate AI-only respondents', us: 'Starts with your own human evidence and validates risky segments with real people' },
  { category: 'Survey platforms', they: 'Collect new answers', us: 'Helps you decide what is already known and what needs fresh fielding' },
] as const

const INDUSTRIES = [
  { slug: 'insurance', demoId: 'insurance-renewal' as DemoId, Icon: Shield, label: 'Insurance', desc: 'Renewal copy, claims communication, agent scripts — pre-read for clarity, trust, and review risk before fielding.' },
  { slug: 'financial-services', demoId: 'financial-services-fees' as DemoId, Icon: TrendingUp, label: 'Financial services', desc: 'Fee disclosures, account-opening flows, advisor scripts — pre-read for clarity, plain-language, and review-risk signals before fielding.' },
  { slug: 'healthcare', demoId: 'healthcare-reminder' as DemoId, Icon: Heart, label: 'Healthcare', desc: 'Patient and member communication — pre-read for comprehension across literacy bands, with plain-language rewrites suggested before rollout.' },
  { slug: 'saas', demoId: 'saas-churn' as DemoId, Icon: Laptop, label: 'SaaS', desc: 'Churn reason validation — joins your survey, behavioral, and outcome data to show which stated reasons match customer behavior.' },
]

const CAROUSEL_DEMOS: { demoId: DemoId; Icon: React.ComponentType<{ className?: string; strokeWidth?: string | number }>; industry: string }[] = [
  { demoId: 'insurance-renewal', Icon: Shield, industry: 'Insurance' },
  { demoId: 'financial-services-fees', Icon: TrendingUp, industry: 'Financial Services' },
  { demoId: 'healthcare-reminder', Icon: Heart, industry: 'Healthcare' },
  { demoId: 'saas-churn', Icon: Laptop, industry: 'SaaS' },
]

const FAQS = [
  { q: 'Is Echosome a synthetic panel?', a: 'No. Echosome uses the research evidence you already have. Predictions cite your real customers\' answers and behavior. When evidence is thin, Echosome routes the question to real-human validation through your panel provider.' },
  { q: 'Is this just generic AI chat for research?', a: 'No. Generic chat summarizes files. Echosome builds structured study memory, runs multiple independent checks with citations, routes risky segments to validation, and learns from real outcomes over time.' },
  { q: 'Does Echosome replace fielding?', a: 'No. Echosome helps you decide what to field. For high-confidence cells, you can ship the decision faster. For risky segments, Echosome can recruit real humans through your panel provider.' },
  { q: 'What data do I need?', a: 'A useful first project can start with a single survey CSV. Better results come from interviews, transcripts, segment data, and behavioral or outcome data. Echosome auto-classifies columns on upload and flags PII before any external model call.' },
  { q: 'Can agencies use this with client work?', a: 'Yes. Echosome supports multi-client workspaces, white-label reports, and a client portal where your client can review pre-reads and validation outcomes read-only.' },
  { q: 'How accurate is it?', a: 'Accuracy depends on evidence quality and validation history. Echosome shows confidence and disagreement rather than pretending every answer is equally reliable. We publish methodology notes as real validation data accumulates — see the methodology page.' },
]

import type React from 'react'

export function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [activeCarousel, setActiveCarousel] = useState(0)
  const carouselPausedRef = useRef(false)
  const prefersReduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  useEffect(() => {
    if (prefersReduced) return
    const interval = setInterval(() => {
      if (!carouselPausedRef.current) setActiveCarousel(prev => (prev + 1) % 4)
    }, 5000)
    return () => clearInterval(interval)
  }, [prefersReduced])

  return (
    <div className="min-h-screen bg-paper">
      <SiteNav />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-12 sm:pt-24 sm:pb-20">
        <div className="max-w-3xl space-y-6">
          <Badge variant="demo" size="md">Now in beta</Badge>
          <h1 className="text-4xl sm:text-5xl font-medium text-deep-ink leading-tight" style={{ letterSpacing: '-0.02em' }}>
            Turn the survey data you already own into a panel you can question.
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Import a CSV. Echosome builds 1:1 digital twins of your respondents, backtests them against held-out answers, and tells you exactly how accurate each twin is before you ask a single new question.
          </p>

          {/* Three proof points */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-2">
            {[
              { stat: 'Backtested', sub: 'Accuracy measured on real holdout before any result is shown' },
              { stat: 'Minutes', sub: 'From CSV to queryable panel — not weeks, not a vendor call' },
              { stat: 'Auditable', sub: 'Every answer cites the respondent evidence behind it' },
            ].map(p => (
              <div key={p.stat} className="rounded-xl border border-border-gray bg-white px-4 py-3">
                <p className="text-base font-medium text-deep-ink">{p.stat}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-snug">{p.sub}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/demo" data-cta="primary">
              <Button variant="primary" size="lg" className="w-full sm:w-auto">Try a live demo (60s)</Button>
            </Link>
            <Link to="/signup" data-cta="secondary">
              <Button variant="outline" size="lg" className="w-full sm:w-auto">Start with your own data</Button>
            </Link>
          </div>

          {/* Audience paths */}
          <div className="flex flex-wrap gap-4 pt-1 text-sm">
            <Link to="/agencies" className="text-echo-indigo hover:underline font-medium">
              For agencies →
            </Link>
            <Link to="/research-teams" className="text-echo-indigo hover:underline font-medium">
              For product &amp; insights teams →
            </Link>
          </div>
        </div>
      </section>

      {/* Demo carousel */}
      <section
        className="max-w-6xl mx-auto px-4 sm:px-6 pb-16"
        data-testid="homepage-demo-carousel"
        onMouseEnter={() => { carouselPausedRef.current = true }}
        onMouseLeave={() => { carouselPausedRef.current = false }}
        aria-label="Industry demo examples"
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CAROUSEL_DEMOS.map(({ demoId, Icon, industry }, idx) => (
            <Link
              key={demoId}
              to={`/app/demo?industry=${demoId}`}
              data-testid="carousel-demo-card"
              data-industry={demoId}
              className={`rounded-xl border p-4 space-y-2 transition-all cursor-pointer ${
                activeCarousel === idx
                  ? 'border-echo-indigo bg-echo-indigo/5 shadow-sm'
                  : 'border-border-gray bg-white hover:border-echo-indigo/40'
              }`}
              onClick={() => setActiveCarousel(idx)}
            >
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-echo-indigo" strokeWidth={1.75} />
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{industry}</span>
              </div>
              <p className="text-xs text-slate-700 leading-snug line-clamp-3">
                {DEMO_APPROVED_HEADLINES[demoId]}
              </p>
              <span className="text-xs text-echo-indigo font-medium">See demo →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Problem section */}
      <section className="bg-soft-panel border-y border-border-gray py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-6">
          <h2 className="text-2xl sm:text-3xl font-medium text-deep-ink" style={{ letterSpacing: '-0.01em' }}>
            Your team already has the evidence. It just cannot reuse it fast enough.
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Past surveys, interviews, support transcripts, and behavioral data are trapped across CSVs, decks, and tools. Generic AI summarizes them but cannot simulate a segment responsibly, cite evidence, flag weak coverage, or route uncertainty to validation.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            Echosome turns scattered evidence into a working pre-read layer — so you ship the decision faster, without skipping the part where humans get to weigh in.
          </p>
        </div>
      </section>

      {/* Five modules */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 space-y-8">
        <h2 className="text-2xl sm:text-3xl font-medium text-deep-ink" style={{ letterSpacing: '-0.01em' }}>Six modules. Each one citable and auditable.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {MODULES.map(mod => (
            <Card key={mod.title} className="flex gap-4 items-start">
              <div className="w-9 h-9 rounded-lg bg-paper-warm border border-border-warm flex items-center justify-center shrink-0">
                <mod.Icon className="w-4.5 h-4.5 text-echo-indigo" strokeWidth={1.75} />
              </div>
              <div>
                <p className="font-medium text-deep-ink text-sm mb-1">{mod.title}</p>
                <p className="text-sm text-slate-600">{mod.desc}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Differentiation table */}
      <section className="bg-soft-panel border-y border-border-gray py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-6">
          <h2 className="text-2xl font-medium text-deep-ink">How Echosome is different</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-gray">
                  <th className="text-left py-3 pr-6 font-medium text-slate-500 w-1/4">Category</th>
                  <th className="text-left py-3 pr-6 font-medium text-slate-500 w-1/3">What they do</th>
                  <th className="text-left py-3 font-medium text-echo-indigo w-5/12">Where Echosome is different</th>
                </tr>
              </thead>
              <tbody>
                {DIFF_TABLE.map(row => (
                  <tr key={row.category} className="border-b border-border-gray/60">
                    <td className="py-3 pr-6 font-medium text-deep-ink">{row.category}</td>
                    <td className="py-3 pr-6 text-slate-600">{row.they}</td>
                    <td className="py-3 text-slate-700">{row.us}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Industries section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 space-y-8">
        <div className="space-y-2">
          <h2 className="text-2xl sm:text-3xl font-medium text-deep-ink" style={{ letterSpacing: '-0.01em' }}>
            Built for research in regulated and complex industries.
          </h2>
          <p className="text-slate-500">Insurance · Financial services · Healthcare · SaaS</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {INDUSTRIES.map(({ slug, demoId, Icon, label, desc }) => (
            <Card key={slug} className="space-y-3">
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-echo-indigo" strokeWidth={1.75} />
                <span className="font-medium text-deep-ink text-sm">{label}</span>
              </div>
              <p className="text-sm text-slate-600">{desc}</p>
              <Link
                to={`/app/demo?industry=${demoId}`}
                className="text-sm text-echo-indigo font-medium hover:underline"
              >
                See the live demo →
              </Link>
            </Card>
          ))}
        </div>
      </section>

      {/* Agency-partner band */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="bg-echo-indigo/5 border border-echo-indigo/20 rounded-2xl p-8 sm:p-10 space-y-5">
          <p className="text-sm font-medium text-echo-indigo uppercase tracking-wide">For agencies and consultancies</p>
          <h2 className="text-2xl font-medium text-deep-ink leading-snug">
            Deliver client engagements on top of Echosome.
          </h2>
          <p className="text-base text-slate-600 leading-relaxed max-w-2xl">
            Spin up client workspaces, build twin panels from your client's research data, run bulk queries across the panel, and ship branded reports with backtest-validated accuracy.
            Your firm owns the client relationship. Echosome powers the underlying modeling, citations, calibration, and audit trail.
          </p>
          <Link to="/agencies">
            <Button variant="outline" size="md">See agency features</Button>
          </Link>
        </div>
      </section>

      {/* Trust section */}
      <section className="bg-soft-panel border-y border-border-gray py-16 sm:py-20" data-testid="trust-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-6">
          <h2 className="text-2xl font-medium text-deep-ink">Built for evidence, not AI theater.</h2>
          <ul className="space-y-3 text-sm text-slate-700">
            <li className="flex items-start gap-2"><span className="text-echo-indigo mt-0.5 shrink-0">→</span>Every prediction cites the uploaded evidence that supports it.</li>
            <li className="flex items-start gap-2"><span className="text-echo-indigo mt-0.5 shrink-0">→</span>Confidence comes from coverage and agreement across checks — never from disagreement.</li>
            <li className="flex items-start gap-2"><span className="text-echo-indigo mt-0.5 shrink-0">→</span>Weak evidence is explicitly flagged; risky segments get routed to validation.</li>
            <li className="flex items-start gap-2"><span className="text-echo-indigo mt-0.5 shrink-0">→</span>Our prediction architecture is based on the Stanford / Google generative-agents framework, adapted for first-party research evidence.</li>
            <li className="flex items-start gap-2"><span className="text-echo-indigo mt-0.5 shrink-0">→</span>Echosome is decision support for research. Final review stays with your legal and compliance teams.</li>
            <li className="flex items-start gap-2"><span className="text-echo-indigo mt-0.5 shrink-0">→</span>Echosome does not claim causal proof. Echosome does not replace real research.</li>
          </ul>
          <Link to="/research/methodology/ablation" className="inline-flex items-center gap-1 text-sm text-echo-indigo font-medium hover:underline">
            Read our methodology →
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20 space-y-4">
        <h2 className="text-2xl font-medium text-deep-ink">Common questions</h2>
        {FAQS.map((faq, i) => (
          <div key={i} className="border border-border-gray rounded-xl overflow-hidden">
            <button
              className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-soft-panel/50 transition-colors"
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              aria-expanded={openFaq === i}
            >
              <span className="font-medium text-deep-ink text-sm">{faq.q}</span>
              <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
            </button>
            {openFaq === i && (
              <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed border-t border-border-gray/60 pt-3">
                {faq.a}
              </div>
            )}
          </div>
        ))}
      </section>

      <SiteFooter />
    </div>
  )
}
